import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useOwnClientId } from "@/hooks/useOwnClientId";
import orbitPortrait from "@/assets/orbit-portrait.png.asset.json";
import orbitAvatar from "@/assets/orbit-avatar.png.asset.json";

const ORANGE = "#FB923C";
const ORANGE_DEEP = "#EA580C";

type Msg = { id?: string; role: "user" | "assistant"; content: string; ts: number };

const SUGGESTIONS = [
  { icon: "📈", label: "What's my biggest SEO opportunity right now?" },
  { icon: "❓", label: "Why isn't this page indexed?" },
  { icon: "🗓", label: "What did Orbit complete this week?" },
  { icon: "🔎", label: "Analyze a page for me" },
  { icon: "🛠", label: "What should I improve manually?" },
  { icon: "🎯", label: "Which page should I focus on next?" },
];

function relTime(ts: number) {
  const s = Math.max(1, Math.floor((Date.now() - ts) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} minute${m === 1 ? "" : "s"} ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
  return new Date(ts).toLocaleDateString();
}

function extractSlug(text: string): string | null {
  const url = text.match(/https?:\/\/[^\s)>\]]+/i);
  if (url) {
    try { return new URL(url[0]).pathname.replace(/^\/+|\/+$/g, ""); } catch { /* fall through */ }
  }
  const path = text.match(/\/[a-z0-9][a-z0-9\-\/]{1,}/i);
  if (path) return path[0].replace(/^\/+|\/+$/g, "");
  return null;
}

const OrbitMark = ({ size = 32 }: { size?: number }) => (
  <span className="relative inline-flex items-center justify-center" style={{ width: size, height: size }} aria-hidden>
    <span className="absolute inset-0 rounded-full" style={{ background: `radial-gradient(circle, ${ORANGE}55 0%, transparent 70%)`, animation: "askorbit-pulse 3.2s ease-in-out infinite" }} />
    <svg width={size * 0.82} height={size * 0.82} viewBox="0 0 32 32" fill="none">
      <circle cx="16" cy="16" r="5" fill={ORANGE} />
      <ellipse cx="16" cy="16" rx="13" ry="5" stroke={ORANGE} strokeWidth="1.4" transform="rotate(-28 16 16)" opacity="0.85" />
      <ellipse cx="16" cy="16" rx="13" ry="5" stroke={ORANGE} strokeWidth="1" transform="rotate(28 16 16)" opacity="0.45" />
    </svg>
    <style>{`@keyframes askorbit-pulse { 0%,100% { opacity:0.55; transform:scale(0.95);} 50% { opacity:1; transform:scale(1.08);} }`}</style>
  </span>
);

const UserAvatar = () => (
  <span className="flex h-8 w-8 items-center justify-center rounded-full" style={{ background: `${ORANGE}22`, border: `1px solid ${ORANGE}55` }} aria-hidden>
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={ORANGE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
    </svg>
  </span>
);

type AskOrbitCardProps = {
  clientId?: string | null;
  clientResolved?: boolean;
};

const AskOrbitCard = ({ clientId, clientResolved = true }: AskOrbitCardProps = {}) => {
  const [value, setValue] = useState("");
  const [messages, setMessages] = useState<Msg[]>([]);
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const { ownClientId } = useOwnClientId("AskOrbitCard");
  const activeClientId = ownClientId ?? clientId ?? null;
  // The page we're currently discussing. Carried across turns so the user
  // never has to repeat "/areas/kyalami" — "that page", "analyze it",
  // "explain that ranking" all resolve to this.
  const activeSlugRef = useRef<string | null>(null);

  // Mark prop as intentionally accepted (kept for API compatibility).
  void clientResolved;

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length, sending]);

  const send = async (q?: string) => {
    const text = (q ?? value).trim();
    if (!text || sending) return;
    const userMsg: Msg = { role: "user", content: text, ts: Date.now() };

    // Update active slug from anything the user pastes.
    const paste = extractSlug(text);
    if (paste) activeSlugRef.current = paste;

    const next = [...messages, userMsg];
    setMessages(next);
    setValue("");
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("ask-orbit", {
        body: {
          messages: next.map((m) => ({ role: m.role, content: m.content })),
          active_slug: activeSlugRef.current,
          ...(activeClientId ? { client_id: activeClientId } : {}),
        },
      });
      if (error) throw error;
      const reply = (data as any)?.reply || (data as any)?.error || "I couldn't generate a reply.";
      const usedSlug = (data as any)?.used_slug;
      if (typeof usedSlug === "string" && usedSlug) activeSlugRef.current = usedSlug;
      setMessages((prev) => [...prev, { role: "assistant", content: reply, ts: Date.now() }]);
    } catch (err: any) {
      toast({ title: "Ask Orbit error", description: err?.message || "Try again", variant: "destructive" });
      setMessages((prev) => [...prev, { role: "assistant", content: "Connection issue — try again.", ts: Date.now() }]);
    } finally {
      setSending(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const mr = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime });
        setTranscribing(true);
        try {
          const b64 = await new Promise<string>((res, rej) => {
            const r = new FileReader();
            r.onloadend = () => res(String(r.result).split(",")[1] || "");
            r.onerror = rej;
            r.readAsDataURL(blob);
          });
          const { data, error } = await supabase.functions.invoke("whisper-transcribe", { body: { audio: b64, mime } });
          if (error) throw error;
          const text = ((data as any)?.text || "").trim();
          if (text) {
            setValue("");
            await send(text);
          } else {
            toast({ title: "No speech detected", description: "Try recording again", variant: "destructive" });
          }
        } catch (err: any) {
          toast({ title: "Transcription failed", description: err?.message || "Try again", variant: "destructive" });
        } finally {
          setTranscribing(false);
        }
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch (err: any) {
      toast({ title: "Microphone unavailable", description: err?.message || "Check permissions", variant: "destructive" });
    }
  };
  const stopRecording = () => { mediaRef.current?.stop(); mediaRef.current = null; setRecording(false); };

  const onAttach = () => fileRef.current?.click();
  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setValue((v) => (v ? v + " " : "") + `[attached: ${f.name}]`);
    e.target.value = "";
  };

  const clearConversation = () => setMessages([]);

  return (
    <section
      className="relative overflow-hidden rounded-[18px] border p-5 sm:p-7"
      style={{
        background: "radial-gradient(120% 140% at 0% 0%, rgba(251,146,60,0.06), transparent 55%), linear-gradient(160deg, #0F1320, #0A0D17)",
        borderColor: `${ORANGE}66`,
        boxShadow: `0 0 0 1px ${ORANGE}1A inset, 0 18px 50px -28px ${ORANGE}55`,
      }}
    >
      {/* Orbit portrait — top-right corner */}
      <img
        src={orbitPortrait.url}
        alt="Orbit"
        aria-hidden
        className="pointer-events-none select-none absolute top-0 right-2 sm:right-4 z-0 h-[150px] sm:h-[200px] md:h-[230px] w-auto object-contain object-top"
      />

      {/* Header */}
      <div className="relative z-10 flex items-start justify-between gap-3 pr-[110px] sm:pr-[150px] md:pr-[180px]">
        <div className="flex items-center gap-3">
          <OrbitMark />
          <h2 className="text-[20px] sm:text-[24px] font-[800] tracking-[-0.015em] text-foreground leading-none">Ask Orbit</h2>
          <span className="hidden sm:inline-block h-4 w-px bg-white/15" />
          <span className="text-[13px] font-[600]" style={{ color: ORANGE }}>Your SEO Manager</span>
        </div>

        {messages.length > 0 && (
          <button
            onClick={clearConversation}
            className="inline-flex items-center gap-1.5 rounded-[8px] border px-2.5 py-1.5 text-[11.5px] font-[600] text-foreground/80 transition-colors hover:text-foreground"
            style={{ background: "rgba(255,255,255,0.02)", borderColor: "rgba(255,255,255,0.1)" }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
            </svg>
            Clear conversation
          </button>
        )}
      </div>

      {/* Intro */}
      <div className="relative z-10 mt-4 pr-[110px] sm:pr-[160px] md:pr-[200px] min-h-[125px] sm:min-h-[175px] md:min-h-[205px]" style={{ animation: "askorbit-intro 0.7s ease-out both" }}>
        <p className="text-[14px] sm:text-[15.5px] leading-[1.65] text-foreground/90 max-w-xl" style={{ fontWeight: 500 }}>
          <span className="font-[700] text-foreground">Hi, I'm Orbit.</span> I work behind the scenes on your SEO every day — tracking your website, analyzing opportunities, and keeping an eye on performance. <span className="font-[600]" style={{ color: ORANGE }}>Ask me anything — I already know the context.</span>
        </p>
        <ul className="mt-4 space-y-2 text-[13.5px] sm:text-[14px] text-foreground/85" style={{ fontWeight: 500 }}>
          {[
            { icon: "🎯", text: "Discover your biggest growth opportunities" },
            { icon: "📈", text: "Understand rankings, traffic, and visibility" },
            { icon: "🔍", text: "Diagnose technical and indexing issues" },
            { icon: "📝", text: "Analyze any page on your website" },
            { icon: "⚡", text: "See what I've done and why I did it" },
          ].map((item, i) => (
            <li
              key={item.text}
              className="flex items-center gap-2.5"
              style={{
                animation: `askorbit-item 0.5s ease-out both`,
                animationDelay: `${0.2 + i * 0.08}s`,
              }}
            >
              <span className="text-[15px] leading-none">{item.icon}</span>
              <span>{item.text}</span>
            </li>
          ))}
        </ul>
        <style>{`
          @keyframes askorbit-intro { 0% { opacity:0; transform: translateY(8px);} 100% { opacity:1; transform: translateY(0);} }
          @keyframes askorbit-item { 0% { opacity:0; transform: translateX(-6px);} 100% { opacity:1; transform: translateX(0);} }
        `}</style>
      </div>

      {/* Conversation */}
      {messages.length > 0 && (
        <div className="mt-5 space-y-3">
          {messages.map((m, i) => (
            <div
              key={i}
              className="rounded-[14px] border p-5 sm:p-6"
              style={{ background: "rgba(8,11,20,0.55)", borderColor: "rgba(255,255,255,0.06)" }}
            >
              <div className="flex items-center gap-2.5">
                {m.role === "user" ? <UserAvatar /> : (
                  <img
                    src={orbitAvatar.url}
                    alt="Orbit"
                    className="h-8 w-8 rounded-full object-cover"
                    style={{ background: `${ORANGE}1A`, boxShadow: `0 0 0 1px ${ORANGE}55, 0 4px 10px -4px ${ORANGE}66` }}
                  />
                )}
                <span className="text-[13px] font-[700] text-foreground">{m.role === "user" ? "You" : "Orbit"}</span>
                <span className="text-[11.5px] text-muted-foreground">{relTime(m.ts)}</span>
              </div>
              <div className="mt-3 text-[18px] text-foreground/90" style={{ lineHeight: 1.7, fontFamily: "'Inter', sans-serif", fontWeight: 500 }}>
                {m.role === "user" ? (
                  <div className="whitespace-pre-wrap">{m.content}</div>
                ) : (
                  <div className="orbit-md">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        p: ({ node, ...props }) => <p className="my-3.5 first:mt-0 last:mb-0" {...props} />,
                        h1: ({ node, ...props }) => <h2 className="mt-6 mb-3 text-[22px] font-[800] tracking-[-0.01em] text-foreground" {...props} />,
                        h2: ({ node, ...props }) => <h3 className="mt-6 mb-3 text-[20px] font-[700] tracking-[-0.005em] text-foreground" {...props} />,
                        h3: ({ node, ...props }) => <h4 className="mt-5 mb-2.5 text-[14.5px] font-[700] uppercase tracking-[0.04em]" style={{ color: ORANGE }} {...props} />,
                        h4: ({ node, ...props }) => <h5 className="mt-5 mb-2 text-[13.5px] font-[700] text-foreground" {...props} />,
                        ul: ({ node, ...props }) => <ul className="my-3.5 ml-1.5 space-y-2 list-none" {...props} />,
                        ol: ({ node, ...props }) => <ol className="my-3.5 ml-6 space-y-2 list-decimal marker:text-muted-foreground marker:font-semibold" {...props} />,
                        li: ({ node, children, ...props }: any) => (
                          <li className="pl-1 leading-[1.7]" {...props}>
                            <span className="relative">
                              <span className="absolute -left-[18px] top-[10px] inline-block h-1.5 w-1.5 rounded-full" style={{ background: ORANGE, opacity: 0.7 }} />
                              {children}
                            </span>
                          </li>
                        ),
                        strong: ({ node, ...props }) => <strong className="font-[700] text-foreground" {...props} />,
                        em: ({ node, ...props }) => <em className="italic text-foreground/80" {...props} />,
                        a: ({ node, ...props }) => <a className="underline underline-offset-2 hover:opacity-80" style={{ color: ORANGE }} target="_blank" rel="noreferrer" {...props} />,
                        code: ({ node, inline, className, children, ...props }: any) =>
                          inline !== false && !String(className || "").includes("language-") ? (
                            <code
                              className="rounded-[6px] px-1.5 py-0.5 font-mono text-[12.5px]"
                              style={{
                                background: `${ORANGE}15`,
                                border: `1px solid ${ORANGE}33`,
                                color: "#FDBA74",
                              }}
                              {...props}
                            >
                              {children}
                            </code>
                          ) : (
                            <code className={className} {...props}>{children}</code>
                          ),
                        pre: ({ node, ...props }) => (
                          <pre
                            className="my-4 overflow-x-auto rounded-[10px] p-3.5 font-mono text-[12.5px] leading-relaxed"
                            style={{ background: "rgba(0,0,0,0.45)", border: "1px solid rgba(255,255,255,0.08)", color: "#F3F4F6" }}
                            {...props}
                          />
                        ),
                        blockquote: ({ node, ...props }) => (
                          <blockquote
                            className="my-4 pl-4 italic text-foreground/75"
                            style={{ borderLeft: `3px solid ${ORANGE}66` }}
                            {...props}
                          />
                        ),
                        hr: () => <hr className="my-5 border-0 h-px" style={{ background: "rgba(255,255,255,0.08)" }} />,
                        table: ({ node, ...props }) => (
                          <div className="my-4 overflow-x-auto rounded-[8px] border" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
                            <table className="w-full text-[13px]" {...props} />
                          </div>
                        ),
                        th: ({ node, ...props }) => <th className="px-3 py-2 text-left font-[700] text-foreground" style={{ background: "rgba(255,255,255,0.04)" }} {...props} />,
                        td: ({ node, ...props }) => <td className="px-3 py-2 border-t" style={{ borderColor: "rgba(255,255,255,0.06)" }} {...props} />,
                      }}
                    >{m.content}</ReactMarkdown>
                  </div>
                )}
              </div>
              {m.role === "assistant" && (
                <div className="mt-3 flex items-center gap-3 text-muted-foreground">
                  <button title="Copy" onClick={() => navigator.clipboard.writeText(m.content)} className="hover:text-foreground">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                  </button>
                  <button title="Helpful" className="hover:text-foreground">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z" /><line x1="7" y1="22" x2="7" y2="11" /></svg>
                  </button>
                  <button title="Not helpful" className="hover:text-foreground">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10z" /><line x1="17" y1="2" x2="17" y2="13" /></svg>
                  </button>
                </div>
              )}
            </div>
          ))}
          {sending && (
            <div className="rounded-[12px] border p-4" style={{ background: "rgba(8,11,20,0.55)", borderColor: "rgba(255,255,255,0.06)" }}>
              <div className="flex items-center gap-2.5">
                <OrbitMark size={28} />
                <span className="text-[13px] font-[700] text-foreground">Orbit</span>
                <span className="text-[11.5px] text-muted-foreground">thinking…</span>
              </div>
              <div className="mt-3 flex gap-1.5">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full" style={{ background: ORANGE, animationDelay: "0s" }} />
                <span className="h-1.5 w-1.5 animate-pulse rounded-full" style={{ background: ORANGE, animationDelay: "0.15s" }} />
                <span className="h-1.5 w-1.5 animate-pulse rounded-full" style={{ background: ORANGE, animationDelay: "0.3s" }} />
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>
      )}

      {/* Input */}
      <div
        className="relative z-10 mt-4 rounded-[14px] border p-2.5"
        style={{ background: "rgba(8,11,20,0.7)", borderColor: "rgba(255,255,255,0.08)", animation: "askorbit-intro 0.8s ease-out both", animationDelay: "0.25s" }}
      >
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder={transcribing ? "Transcribing…" : "Ask Orbit anything…"}
          disabled={transcribing || sending}
          className="w-full bg-transparent text-[14px] text-foreground placeholder:text-muted-foreground/60 outline-none px-2 py-2"
        />
        <div className="mt-2 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <input ref={fileRef} type="file" className="hidden" onChange={onFile} />
            <button
              type="button" onClick={onAttach} aria-label="Attach file"
              className="flex h-9 w-9 items-center justify-center rounded-[9px] text-muted-foreground transition-colors hover:text-foreground"
              style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" /></svg>
            </button>
            <button
              type="button" onClick={recording ? stopRecording : startRecording}
              aria-label={recording ? "Stop recording" : "Record voice"}
              className="flex h-9 w-9 items-center justify-center rounded-[9px] transition-colors"
              style={{
                background: recording ? `${ORANGE}1A` : "rgba(255,255,255,0.03)",
                border: `1px solid ${recording ? ORANGE : "rgba(255,255,255,0.06)"}`,
                color: recording ? ORANGE : "rgba(229,231,235,0.7)",
              }}
            >
              {recording ? (
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-70" style={{ background: ORANGE }} />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-sm" style={{ background: ORANGE }} />
                </span>
              ) : (
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="2" width="6" height="12" rx="3" /><path d="M5 10v2a7 7 0 0 0 14 0v-2" /><line x1="12" y1="19" x2="12" y2="22" /></svg>
              )}
            </button>
          </div>
          <button
            type="button" onClick={() => send()} aria-label="Send"
            disabled={!value.trim() || sending}
            className="flex h-10 w-12 items-center justify-center rounded-[10px] transition-all disabled:opacity-50"
            style={{
              background: `linear-gradient(135deg, ${ORANGE} 0%, ${ORANGE_DEEP} 100%)`,
              color: "#1a0a00",
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M2.5 21.5 22 12 2.5 2.5 2.5 10l13 2-13 2z" /></svg>
          </button>
        </div>
      </div>

      {/* Suggestion chips */}
      <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {SUGGESTIONS.map((s) => (
          <button
            key={s.label}
            onClick={() => send(s.label)}
            disabled={sending}
            className="inline-flex items-center gap-2 rounded-[10px] border px-3 py-2 text-left text-[12.5px] font-[600] text-foreground/80 transition-all hover:text-foreground disabled:opacity-60"
            style={{ background: "rgba(255,255,255,0.02)", borderColor: "rgba(255,255,255,0.08)" }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${ORANGE}66`; e.currentTarget.style.background = `${ORANGE}0F`; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"; e.currentTarget.style.background = "rgba(255,255,255,0.02)"; }}
          >
            <span className="text-[13px] leading-none">{s.icon}</span>
            <span className="truncate">{s.label}</span>
          </button>
        ))}
      </div>

      <div className="mt-5 text-center text-[11px] text-muted-foreground/60">
        Orbit uses real data from your website and connected sources.
      </div>
    </section>
  );
};

export default AskOrbitCard;
